import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import styles from './GroupedStackedBarChart.module.scss';
import { useAppSelector } from '../../../redux/hooks';
import KPIsPill from '../KPIsPill/KPIsPill';
import { useTranslation } from 'react-i18next';

/* --- Types & Constants --- */
type Theme = 'light' | 'dark';
interface Segment {
  label: string;
  value: number;
}
interface BarData {
  label: string;
  segments: Segment[];
}
interface Props {
  data: BarData[];
  showPills?: boolean;
  CATEGORY_COLORS?: Record<Theme, string[]>;
  THEME_STYLE?: Record<Theme, any>;
  isShowAllPill?: boolean;
  showLegend?: boolean;
}

const DEFAULT_CATEGORY_COLORS: Record<Theme, string[]> = {
  light: ['#99B2F5', '#F16A81', '#77CBBF', '#667DBB', '#B2F9FF', '#AF60B1', '#F5A066', '#CE4800'],
  dark: ['#5969D6', '#F3596C', '#396E75', '#38539A', '#05A896', '#7E3E80', '#F5A066', '#EF6D46'],
};

const DEFAULT_THEME_STYLE = {
  light: { axis: '#707070', grid: '#e4e4e4', tooltipBg: '#ffffff', tooltipText: '#2c2c2c' },
  dark: { axis: '#aeaeb2', grid: '#636366', tooltipBg: '#1c1c1e', tooltipText: '#d8d8d9' },
};

export default function StackedGroupedBarChart({
  data,
  showPills = true,
  CATEGORY_COLORS = DEFAULT_CATEGORY_COLORS,
  THEME_STYLE = DEFAULT_THEME_STYLE,
  isShowAllPill = true,
  showLegend = false,
}: Props) {
  const { t, i18n } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme) as Theme;
  const colors = CATEGORY_COLORS[currentTheme] || CATEGORY_COLORS.light;
  const stylesEnv = THEME_STYLE[currentTheme] || THEME_STYLE.light;

  const { years, categories, categoryKeys } = useMemo(() => {
    const yearsList: string[] = [];
    const catMap: Record<string, { label: string; budget: number[]; bv: number[] }> = {};

    // Extract unique categories first
    data.forEach((item) =>
      item.segments.forEach((seg) => {
        if (!catMap[seg.label]) {
          catMap[seg.label] = { label: seg.label, budget: [], bv: [] };
        }
      }),
    );

    // Process pairs of Budget/BV
    for (let i = 0; i < data.length; i += 2) {
      const budgetItem = data[i];
      const bvItem = data[i + 1];
      if (!budgetItem || !bvItem) break;

      yearsList.push(budgetItem.label);
      Object.keys(catMap).forEach((key) => {
        const bSeg = budgetItem.segments.find((s) => s.label === key);
        const vSeg = bvItem.segments.find((s) => s.label === key);
        catMap[key].budget.push(bSeg?.value || 0);
        catMap[key].bv.push(vSeg?.value || 0);
      });
    }

    return { years: yearsList, categories: catMap, categoryKeys: Object.keys(catMap) };
  }, [data]);

  const [selected, setSelected] = useState<string>(
    isShowAllPill ? 'ALL' : categoryKeys[0] || 'ALL',
  );
  const effectiveSelection = showPills ? selected : 'ALL';

  // Memoized Chart Options
  const option = useMemo(() => {
    const isAll = effectiveSelection === 'ALL';
    const activeKeys = isAll ? categoryKeys : [effectiveSelection];

    const series = activeKeys.flatMap((key) => {
      const idx = categoryKeys.indexOf(key);
      const category = categories[key];
      const color = colors[idx % colors.length];
      const isLast = idx === activeKeys.length - 1;

      return [
        {
          name: category.label,
          type: 'bar',
          stack: isAll ? 'budget' : undefined,
          barWidth: isAll ? 24 : 32,
          itemStyle: { color, borderRadius: [4, 4, 0, 0] },
          label: {
            show: isAll ? isLast : true,
            position: 'top',
            formatter: '$',
            color: stylesEnv.axis,
          },
          data: category.budget,
        },
        {
          name: category.label,
          type: 'bar',
          stack: isAll ? 'bv' : undefined,
          barWidth: isAll ? 24 : 32,
          itemStyle: {
            color: 'transparent',
            borderColor: color,
            borderWidth: 1,
            borderRadius: [4, 4, 0, 0],
          },
          label: {
            show: isAll ? isLast : true,
            position: 'top',
            formatter: 'BV',
            color: stylesEnv.axis,
          },
          data: category.bv,
        },
      ];
    });

    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: stylesEnv.tooltipBg,
        textStyle: { color: stylesEnv.tooltipText, fontSize: 13 },
        formatter: (params: any) => {
          const isBV = params.seriesIndex % 2 === 1;
          return `${params.seriesName}<br/>${params.value.toLocaleString()} ${isBV ? 'BV' : '$'}`;
        },
      },
      legend: showLegend
        ? {
            bottom: 0,
            icon: 'circle',
            textStyle: { color: stylesEnv.axis },
            data: activeKeys.map((k) => categories[k].label),
          }
        : undefined,
      grid: { top: 40, left: 10, right: 10, bottom: 40, containLabel: true },
      xAxis: {
        type: 'category',
        data: years,
        axisLabel: { color: stylesEnv.axis },
        axisLine: { lineStyle: { color: stylesEnv.grid } },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: stylesEnv.grid } },
        axisLabel: { color: stylesEnv.axis },
      },
      series,
    };
  }, [categories, categoryKeys, colors, stylesEnv, effectiveSelection, years, showLegend]);

  return (
    <div className={styles.container}>
      {showPills && (
        <div className={styles.pillsOuterContainer}>
          {isShowAllPill && (
            <KPIsPill
              code="ALL"
              label="All"
              onPillClick={setSelected}
              isSelected={selected === 'ALL'}
            />
          )}
          {categoryKeys.map((key, idx) => {
            const label = categories[key].label.toLowerCase();

            const translated = i18n.exists(`categoryGroups.${label}`)
              ? t(`categoryGroups.${label}`)
              : label;

            return (
              <KPIsPill
                key={key}
                code={key}
                label={translated}
                onPillClick={setSelected}
                isSelected={selected === key}
                dotColor={colors[idx % colors.length]}
              />
            );
          })}
        </div>
      )}
      <ReactECharts option={option} notMerge={true} lazyUpdate={true} style={{ height: 420 }} />
    </div>
  );
}
